import { useCallback, useRef } from 'react'
import SubmitOverlay from '@/components/SubmitOverlay'

// Save feedback for a screen that owns SEVERAL mutations.
//
// The audit's finding: of 79 mutations in the labour module, 58 closed their
// dialog — or simply did nothing visible — with no confirmation that anything
// had been written. Closing a modal is not confirmation (K-forms §6); an
// operator who taps Save on a marginal connection has no way to tell a
// success from a silent failure, and the second tap is how a duplicate gets
// written.
//
// One <SubmitOverlay/> per mutation would mean scattering five of them through
// a page and hoping none is forgotten. This renders exactly one — for whichever
// mutation is currently doing something — so a screen gets feedback for ALL its
// writes from a single mount point.
//
// Only one mutation can be mid-flight in practice (each is fired by a tap and
// the buttons disable while pending), so "the first that is active" is not a
// tie-break in disguise; it is the one the operator just triggered.
//
//   <MutationOverlays of={[
//     [save,   'Shift saved'],
//     [remove, 'Shift deleted'],
//   ]} />

export type OverlayFlags = {
  isPending: boolean
  isSuccess: boolean
  isError:   boolean
  error?:    unknown
  reset:     () => void
}
type Flags = OverlayFlags

/** The overlay shows pending + the tick only; a failure is the mutation's own
 *  onError's to answer. For a write whose failure is NOT a plain red card — a
 *  stale write that offers to load the other user's version, a two-step save
 *  whose second step failed and must NOT read as "Save failed". Without this
 *  the operator gets that dialog AND a red card for the same event.
 *
 *  The other shape — a refusal that settles as a SUCCESS (`removed: 0`,
 *  `'cancelled'`) — is not this: hand the overlay a flags object with
 *  `isSuccess` narrowed, e.g. `const xFeedback = { isPending: x.isPending,
 *  isSuccess: x.isSuccess && x.data?.removed > 0, … }`; the detector credits
 *  that alias. */
export function errorHandledElsewhere(m: OverlayFlags): OverlayFlags {
  return m.isError ? { ...m, isError: false, error: undefined } : m
}

/** Flags for a write whose confirm is asked INSIDE the mutationFn.
 *
 *  react-query settles a DECLINED confirm as a success, so feeding one
 *  straight to the overlay ticks "Line deleted" at the operator who just
 *  said No — the exact false confirmation this sweep exists to remove. Such a
 *  mutationFn returns `false` when it backed out and `true` when it wrote;
 *  this withholds the tick for anything but `true`, and leaves a real failure
 *  alone. (The other half of the pair is the `'cancelled'` sentinel, which
 *  Estimates and Dispatch answer with their own local `exceptCancelled`.) */
export function onlyWhenDone(m: Flags & { data?: unknown }): Flags {
  return m.data === true ? m : { ...m, isSuccess: false }
}

export default function MutationOverlays({ of, inline = false, domain }: {
  /** [mutation, success wording]. The wording is what the operator reads on
   *  the green tick, so name the THING that was saved, not "Saved". */
  of: ReadonlyArray<readonly [Flags, string] | readonly [Flags, string, { label: string; onSelect: () => void } | undefined]>
  /** SubmitOverlay's `inline`: a badge where this is mounted, no backdrop —
   *  for writes made from a cell or a row. */
  inline?: boolean
  /** SubmitOverlay's `domain`: the flow's friendlyError registry, for a
   *  server refusal that arrives with an ordinary PG code (packhouseErrorDomains.ts). */
  domain?: [RegExp, string][]
}) {
  const active = of.find(([m]) => m.isPending || m.isSuccess || m.isError)
  // ONE dismiss callback for the life of the mount, reading the latest active
  // mutation. The overlay re-arms its 1.2 s success timer whenever onDismiss
  // changes, and a fresh arrow per render meant a screen re-rendering faster
  // than that — a capture form fed by a recording walk's GPS, a fix every
  // second or two — held the tick, and its tap-blocking backdrop, up for as
  // long as the fixes kept coming. Quick log staying open for the next find
  // (FM02) is where that would have stopped a scout cold.
  const resetRef = useRef<(() => void) | undefined>(undefined)
  resetRef.current = active?.[0].reset
  const onDismiss = useCallback(() => resetRef.current?.(), [])
  if (!active) return null
  const [m, successLabel, nextStep] = active
  return (
    <SubmitOverlay
      isPending={m.isPending}
      isSuccess={m.isSuccess}
      isError={m.isError}
      error={m.error}
      successLabel={successLabel}
      nextStep={nextStep}
      onDismiss={onDismiss}
      inline={inline}
      domain={domain}
    />
  )
}
